import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { hasValidAssignmentPolygon } from "../api/assignmentArea";
import { getAssignments, useDeleteAssignment } from "../api/assignments";
import { exportAssignmentsAsA4Images } from "./exportAssignmentImages";
import { AssignmentForm } from "./AssignmentForm";
import { useMunicipalities } from "../api/map";
import { useMapContextOptional } from "../map/MapContext";
import "./assignments.css";

interface AssignmentsPanelProps {
    onMunicipalityChange?: (id: string, code: string) => void;
}

export const AssignmentsPanel = ({ onMunicipalityChange }: AssignmentsPanelProps = {}): JSX.Element => {
    const { t } = useTranslation();
    const mapContext = useMapContextOptional();
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<string>("");
    const [isExporting, setIsExporting] = useState(false);

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["assignments", selectedMunicipalityId],
        queryFn: () => getAssignments(undefined, undefined, selectedMunicipalityId),
        enabled: !!selectedMunicipalityId,
        staleTime: 60_000,
        retry: 2
    });

    const municipalitiesQuery = useMunicipalities(
        { limit: 2000, includeGeometry: false },
        null,
        { enabled: true }
    );

    const municipalities = useMemo(
        () =>
            (municipalitiesQuery.data?.features ?? [])
                .map((feature) => ({
                    id: String(feature.id ?? feature.properties.municipalityPolygonId),
                    name: feature.properties.name,
                    code: feature.properties.code
                }))
                .sort((a, b) => a.name.localeCompare(b.name, "sv")),
        [municipalitiesQuery.data?.features]
    );

    const totalTargetAddresses = useMemo(
        () => (data ?? []).reduce((sum, assignment) => sum + (assignment.targetAddressCount ?? 0), 0),
        [data]
    );

    const deleteMutation = useDeleteAssignment();

    const handleDelete = async (id: string) => {
        if (window.confirm(t("assignments.actions.confirmDelete"))) {
            deleteMutation.mutate(id, {
                onSuccess: () => {
                    refetch();
                }
            });
        }
    };

    const handleCreateSuccess = () => {
        mapContext?.setPolygonArea(null);
        setIsCreating(false);
        refetch();
    };

    const handleEditSuccess = () => {
        mapContext?.setPolygonArea(null);
        setEditingId(null);
        refetch();
    };

    const editingAssignment = editingId ? data?.find((a) => a.id === editingId) : undefined;
    const exportDisabled =
        isExporting || isLoading || !selectedMunicipalityId || !data || data.length === 0;

    const handleExportA4Images = async () => {
        if (!data || data.length === 0) {
            return;
        }

        setIsExporting(true);
        try {
            const result = await exportAssignmentsAsA4Images(data, ({ current, total, name }) => {
                toast.loading(`Exporterar ${current}/${total}: ${name}`, { id: "assignments-export" });
            });
            toast.success(
                `Klart: ${result.exported} bilder exporterade${result.skipped > 0 ? `, ${result.skipped} hoppades över` : ""}.`,
                { id: "assignments-export" }
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : "Export misslyckades.";
            toast.error(message, { id: "assignments-export" });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <section className="app-panel" aria-labelledby="assignments-panel__title">
            <header className="app-panel__header">
                <h2 id="assignments-panel__title" className="app-panel__title">
                    {t("assignments.title")}
                </h2>
                <p className="app-panel__description">{t("assignments.description")}</p>
            </header>

            {isCreating && (
                <div className="assignments-form-container">
                    <AssignmentForm
                        defaultMunicipalityPolygonId={selectedMunicipalityId}
                        onSuccess={handleCreateSuccess}
                        onCancel={() => {
                            mapContext?.setPolygonArea(null);
                            setIsCreating(false);
                        }}
                    />
                </div>
            )}

            {editingId && editingAssignment && (
                <div className="assignments-form-container">
                    <AssignmentForm
                        assignment={editingAssignment}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            mapContext?.setPolygonArea(null);
                            setEditingId(null);
                        }}
                    />
                </div>
            )}

            {!isCreating && !editingId && (
                <div className="assignments-list">
                    <label className="assignments-panel__municipality-filter">
                        {t("assignments.fields.municipality", "Kommun")}
                        <select
                            value={selectedMunicipalityId}
                            onChange={(event) => {
                                mapContext?.setHighlightedAssignmentArea(null);
                                const id = event.target.value;
                                setSelectedMunicipalityId(id);
                                const code = municipalities.find((m) => m.id === id)?.code ?? "";
                                onMunicipalityChange?.(id, code);
                            }}
                        >
                            <option value="">{t("assignments.placeholders.municipality", "Välj kommun")}</option>
                            {municipalities.map((municipality) => (
                                <option key={municipality.id} value={municipality.id}>
                                    {municipality.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    {!selectedMunicipalityId && (
                        <p className="assignments-list__hint">
                            {t("assignments.states.selectMunicipalityFirst", "Välj kommun för att visa uppdrag.")}
                        </p>
                    )}

                    {selectedMunicipalityId && !isCreating && !editingId && (
                        <div className="assignments-panel__actions">
                            <button
                                type="button"
                                className="assignments-panel__create-button"
                                onClick={() => {
                                    mapContext?.setHighlightedAssignmentArea(null);
                                    setIsCreating(true);
                                }}
                            >
                                {t("assignments.actions.create")}
                            </button>
                            <button
                                type="button"
                                className="assignments-panel__export-button"
                                disabled={exportDisabled}
                                onClick={handleExportA4Images}
                                title="Exportera en A4 liggande PNG per uppdrag i vald kommun"
                            >
                                {isExporting ? "Exporterar..." : "Exportera A4-bilder"}
                            </button>
                        </div>
                    )}

                    {isLoading && <p>{t("assignments.states.loading")}</p>}
                    {isError && (
                        <p role="alert" className="assignments-list__error">
                            {t("assignments.states.error")}
                            {" "}
                            <button type="button" className="assignments-list__retry-button" onClick={() => refetch()}>
                                Försök igen
                            </button>
                        </p>
                    )}
                    {!isLoading && !isError && selectedMunicipalityId && data?.length === 0 && (
                        <p>{t("assignments.states.empty")}</p>
                    )}

                    {data?.map((assignment) => (
                        <article key={assignment.id} className="assignments-card">
                            <header className="assignments-card__header">
                                <h3>{assignment.name}</h3>
                                {assignment.targetAddressCount !== undefined && (
                                    <span className="assignments-card__count">{assignment.targetAddressCount}</span>
                                )}
                            </header>
                            {assignment.distributorName && (
                                <p className="assignments-card__distributor">{assignment.distributorName}</p>
                            )}
                            <div className="assignments-card__actions">
                                {assignment.status === "planned" &&
                                    mapContext &&
                                    hasValidAssignmentPolygon(assignment.area) && (
                                        <button
                                            type="button"
                                            className="assignments-card__show-area-button"
                                            onClick={() =>
                                                mapContext.setHighlightedAssignmentArea(assignment.area!, {
                                                    assignmentId: assignment.id
                                                })
                                            }
                                        >
                                            {t("assignments.actions.showAreaOnMapShort", "Visa")}
                                        </button>
                                    )}
                                <button
                                    type="button"
                                    className="assignments-card__edit-button"
                                    onClick={() => {
                                        // Show existing area on map while editing (cleared when drawing starts)
                                        mapContext?.setHighlightedAssignmentArea(assignment.area ?? null, {
                                            assignmentId: assignment.id
                                        });
                                        setEditingId(assignment.id);
                                    }}
                                >
                                    {t("assignments.actions.edit")}
                                </button>
                                <button
                                    type="button"
                                    className="assignments-card__delete-button"
                                    onClick={() => handleDelete(assignment.id)}
                                    disabled={deleteMutation.isPending}
                                >
                                    {deleteMutation.isPending ? t("assignments.actions.deleting") : t("assignments.actions.delete")}
                                </button>
                            </div>
                        </article>
                    ))}

                    {selectedMunicipalityId && !isLoading && !isError && (
                        <p className="assignments-list__sum">
                            {t("assignments.fields.totalAddresses", "Totalt antal adresser")}: {totalTargetAddresses}
                        </p>
                    )}
                </div>
            )}
        </section>
    );
};

export default AssignmentsPanel;